import { createAsyncThunk } from "@reduxjs/toolkit"

import { handleApiError } from "../../../Utils/thunkHelpers"
import * as services from "../services"
import { DASHBOARD_SLICE_NAME } from "./dashboard.constants"

// The networks run in a fixed order rather than by volume. RDR carries the biggest and
// straightest flow, so it sits on top and its ribbon runs across the diagram without
// crossing anything; CDRN and Ethoca fan out beneath it. Sorting by count would reshuffle
// the whole picture every time the window changed.
const SERVICE_ORDER = ["rdr", "cdrn", "ethoca"]

// Shape the already-aggregated source -> outcome rows (CHA1-2007: one row per
// alert_service x alert_status, with its own count/volume - no client-side tallying of
// raw alerts anymore) into the nodes/links the Sankey draws. Built from what the data
// actually contains (never a fixed list), so every source and every status the account
// produces shows up, each carrying its count and its order value.
export const buildAlertsFlow = (data) => {
  const rows = data?.result || []

  const pairs = new Map() // service -> Map(status -> {count, volume})
  const serviceTotals = new Map()
  const statusTotals = new Map()
  const serviceVolumes = new Map()
  const statusVolumes = new Map()
  let totalVolume = 0

  const add = (map, key, amount) => map.set(key, (map.get(key) ?? 0) + amount)

  rows.forEach((row) => {
    const service = row?.alert_service
    const status = row?.alert_status
    if (!service || !status) return
    const count = Number(row?.count) || 0
    const volume = Number(row?.volume) || 0

    if (!pairs.has(service)) pairs.set(service, new Map())
    pairs.get(service).set(status, { count, volume })

    add(serviceTotals, service, count)
    add(statusTotals, status, count)
    add(serviceVolumes, service, volume)
    add(statusVolumes, status, volume)
    totalVolume += volume
  })

  const byCountDesc = (a, b) => b[1] - a[1]
  const known = SERVICE_ORDER.filter((id) => serviceTotals.has(id))
  // Anything the account produces that is not one of the three known networks (PayPal,
  // say) keeps the old volume ordering and follows them.
  const extra = [...serviceTotals.entries()]
    .filter(([id]) => !SERVICE_ORDER.includes(id))
    .sort(byCountDesc)
    .map(([id]) => id)
  const services = [...known, ...extra]
  const statuses = [...statusTotals.entries()].sort(byCountDesc).map(([id]) => id)

  // A single root carries the whole population, so the chart opens on a 100% anchor
  // and reads left to right: all alerts -> the network that raised them -> outcome.
  const nodes = [
    ...(services.length
      ? [{ id: "all", kind: "all", value: "All alerts", volume: totalVolume }]
      : []),
    ...services.map((id) => ({
      id: `service:${id}`,
      kind: "service",
      value: id,
      volume: serviceVolumes.get(id) ?? 0
    })),
    ...statuses.map((id) => ({
      id: `status:${id}`,
      kind: "status",
      value: id,
      volume: statusVolumes.get(id) ?? 0
    }))
  ]

  // Emit links source-major so each node's ribbons leave in a stable, uncrossed order.
  const links = services.map((service) => ({
    source: "all",
    target: `service:${service}`,
    value: serviceTotals.get(service),
    volume: serviceVolumes.get(service) ?? 0
  }))
  services.forEach((service) => {
    statuses.forEach((status) => {
      const cell = pairs.get(service)?.get(status)
      if (cell?.count) {
        links.push({
          source: `service:${service}`,
          target: `status:${status}`,
          value: cell.count,
          volume: cell.volume
        })
      }
    })
  })

  return { nodes, links }
}

export const getAlertOverviewData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alert_overview_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsDescriptorData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_descriptor_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsAccountData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_account_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsCountryData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_country_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsBankData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_bank_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsCardTypeData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_card_type_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getUsersBySourceData = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_users_by_source_data`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAllBusinessAccountsList = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_all_business_accounts_list`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getAllBusinessAccountsList()
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getDashboardStatistics = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_dashboard_statistics`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardStatistics(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getCreditsSummary = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_credits_summary`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getDashboardData(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

// Custom saga logic (getAlertsFlowSaga): the joint alert_service x alert_status rows are
// shaped into Sankey nodes/links before being handed to the slice, exactly like the saga
// built `buildAlertsFlow(response.data)` before dispatching success — and, matching the
// saga, a failed/errored response resolves with no error notification (no
// `ErrorsActions.errorSave` dispatch here, same as the original catch-all `getAlertsFlowFailure`).
export const getAlertsFlow = createAsyncThunk(
  `${DASHBOARD_SLICE_NAME}/get_alerts_flow`,
  async (params, { rejectWithValue }) => {
    const response = await services.getAlertsFlow(params)
    if (!response.ok || !response.data) {
      return rejectWithValue()
    }
    return buildAlertsFlow(response.data)
  }
)
